import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './i18n';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Catalog from './pages/Catalog';
import About from './pages/About';
import Contacts from './pages/Contacts';
import AdminDashboardPro from './pages/AdminDashboardPro';
import AdminOrders from './pages/AdminOrders';
import AdminClientsPro from './pages/AdminClientsPro';
import AdminContainers from './pages/AdminContainers';
import AdminContainersConfig from './pages/AdminContainersConfig';
import AdminRouteEditor from './pages/AdminRouteEditor';
import AdminLegalPages from './pages/AdminLegalPages';
import AdminContracts from './pages/AdminContracts';
import LegalDocument from './pages/LegalDocument';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailure from './pages/PaymentFailure';
import AdminProductsPro from './pages/AdminProductsPro';
import AdminPriceList from './pages/AdminPriceList';
import AdminSettings from './pages/AdminSettings';
import AdminPayments from './pages/AdminPayments';
import AdminFinance from './pages/AdminFinance';
import AdminChurn from './pages/AdminChurn';
import AdminSupport from './pages/AdminSupport';
import AdminDelivery from './pages/AdminDelivery';
import AdminIntegrations from './pages/AdminIntegrations';
import AdminSecurity from './pages/AdminSecurity';
import AdminNotifications from './pages/AdminNotifications';
import AdminDiscountGroups from './pages/AdminDiscountGroups';
import AdminCities from './pages/AdminCities';
import AdminCityPrices from './pages/AdminCityPrices';
import AdminContentCreators from './pages/AdminContentCreators';
import AdminContent from './pages/AdminContent';
import MediaStudio from './pages/MediaStudio';
import MyOrders from './pages/MyOrders';
import AnalyticsPowerBI from './pages/AnalyticsPowerBI';
import MyAddresses from './pages/MyAddresses';
import LegalEntities from './pages/LegalEntities';
import SharedGallery from './pages/SharedGallery';
import AdminDeliveryZones from './pages/AdminDeliveryZones';
import B2BNetworks from './pages/B2BNetworks';
import B2BMenu from './pages/B2BMenu';
import { Toaster } from './components/ui/sonner';
import SupportWidget from './components/SupportWidget';
import { isAdminUser, hasPermission } from './utils/permissions';
import DriverApp from './pages/DriverApp';
import DeliveryPointPublic from './pages/DeliveryPointPublic';
import AnalyticsApp from './pages/AnalyticsApp';
import AdminAnalyticsViewers from './pages/AdminAnalyticsViewers';
import AdminMcpDocs from './pages/AdminMcpDocs';
import ProductionApp from './pages/ProductionApp';
import FinanceApp from './pages/FinanceApp';
import SupportApp from './pages/SupportApp';
import TgSupportRedirect from './pages/TgSupportRedirect';
import AdminProduction from './pages/AdminProduction';
import AdminInventoryImport from './pages/AdminInventoryImport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper to get current language for redirects
const getCurrentLang = () => localStorage.getItem('i18nextLng') || 'ru';

// Language-aware redirect component
function LangRedirect({ to = '' }) {
  const lang = getCurrentLang();
  const location = useLocation();
  // Preserve query string (?token=...) and hash so reset/verification links keep working
  return <Navigate to={`/${lang}${to}${location.search}${location.hash}`} replace />;
}

// Language route wrapper component
function LanguageWrapper({ children }) {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && ['ru', 'kz', 'en'].includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
      // Always sync localStorage with URL language
      localStorage.setItem('i18nextLng', lang);
    }
  }, [lang, i18n]);

  return children;
}

// Main App content with routing
function AppRoutes({ user, setUser, onLogout, loading }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-xl text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Telegram Mini App для водителей — без языкового префикса и общего layout */}
      <Route path="/driver" element={<DriverApp />} />
      <Route path="/point/:token" element={<DeliveryPointPublic />} />
      {/* Telegram Mini App «Аналитика» — без языкового префикса и общего layout */}
      <Route path="/analytics-app" element={<AnalyticsApp />} />
      <Route path="/production-app" element={<ProductionApp />} />
      <Route path="/finance-app" element={<FinanceApp />} />
      <Route path="/support-app" element={<SupportApp />} />
      <Route path="/tg-support" element={<TgSupportRedirect />} />

      {/* Redirect root to saved/default language */}
      <Route path="/" element={<LangRedirect />} />
      
      {/* Language-prefixed routes */}
      <Route path="/:lang" element={
        <LanguageWrapper>
          {user?.role === 'ContentCreator' 
            ? <Navigate to={`/${getCurrentLang()}/media-studio`} replace /> 
            : <Catalog user={user} onLogout={onLogout} setUser={setUser} />}
        </LanguageWrapper>
      } />
      <Route path="/:lang/about" element={
        <LanguageWrapper>
          {user?.role === 'ContentCreator' 
            ? <Navigate to={`/${getCurrentLang()}/media-studio`} replace /> 
            : <About user={user} onLogout={onLogout} />}
        </LanguageWrapper>
      } />
      <Route path="/:lang/contacts" element={
        <LanguageWrapper>
          {user?.role === 'ContentCreator' 
            ? <Navigate to={`/${getCurrentLang()}/media-studio`} replace /> 
            : <Contacts user={user} onLogout={onLogout} />}
        </LanguageWrapper>
      } />
      <Route path="/:lang/login" element={<LanguageWrapper>{!user ? <Login setUser={setUser} /> : <Navigate to={user?.role === 'ContentCreator' ? `/${getCurrentLang()}/media-studio` : `/${getCurrentLang()}`} />}</LanguageWrapper>} />
      <Route path="/:lang/register" element={<LanguageWrapper>{!user ? <Register setUser={setUser} /> : <Navigate to={user?.role === 'ContentCreator' ? `/${getCurrentLang()}/media-studio` : `/${getCurrentLang()}`} />}</LanguageWrapper>} />
      <Route path="/:lang/forgot-password" element={<LanguageWrapper>{!user ? <ForgotPassword /> : <Navigate to={user?.role === 'ContentCreator' ? `/${getCurrentLang()}/media-studio` : `/${getCurrentLang()}`} />}</LanguageWrapper>} />
      <Route path="/:lang/reset-password" element={<LanguageWrapper><ResetPassword /></LanguageWrapper>} />
      
      {/* Protected pages - login required, ContentCreator redirected to media-studio */}
      <Route path="/:lang/profile" element={<LanguageWrapper>{!user ? <Navigate to={`/${getCurrentLang()}/login`} /> : user?.role === 'ContentCreator' ? <Navigate to={`/${getCurrentLang()}/media-studio`} replace /> : <Profile user={user} setUser={setUser} onLogout={onLogout} />}</LanguageWrapper>} />
      <Route path="/:lang/settings" element={<LanguageWrapper>{!user ? <Navigate to={`/${getCurrentLang()}/login`} /> : user?.role === 'ContentCreator' ? <Navigate to={`/${getCurrentLang()}/media-studio`} replace /> : <Settings user={user} setUser={setUser} onLogout={onLogout} />}</LanguageWrapper>} />
      <Route path="/:lang/orders" element={<LanguageWrapper>{!user ? <Navigate to={`/${getCurrentLang()}/login`} /> : user?.role === 'ContentCreator' ? <Navigate to={`/${getCurrentLang()}/media-studio`} replace /> : <MyOrders user={user} onLogout={onLogout} />}</LanguageWrapper>} />
      <Route path="/:lang/addresses" element={<LanguageWrapper>{!user ? <Navigate to={`/${getCurrentLang()}/login`} /> : user?.role === 'ContentCreator' ? <Navigate to={`/${getCurrentLang()}/media-studio`} replace /> : <MyAddresses user={user} onLogout={onLogout} />}</LanguageWrapper>} />
      <Route path="/:lang/legal-entities" element={<LanguageWrapper>{!user ? <Navigate to={`/${getCurrentLang()}/login`} /> : user?.role === 'ContentCreator' ? <Navigate to={`/${getCurrentLang()}/media-studio`} replace /> : <LegalEntities user={user} onLogout={onLogout} />}</LanguageWrapper>} />
      <Route path="/:lang/analytics" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'analytics.view') ? <AnalyticsPowerBI user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      
      {/* Admin pages with RBAC - permission-based access control */}
      <Route path="/:lang/admin" element={<LanguageWrapper>{isAdminUser(user) ? <AdminDashboardPro user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/orders" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'orders.view') ? <AdminOrders user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/clients" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'clients.view') ? <AdminClientsPro user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/churn" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, ['analytics.view', 'clients.view']) ? <AdminChurn user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/analytics-access" element={<LanguageWrapper>{isAdminUser(user) ? <AdminAnalyticsViewers user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/mcp-docs" element={<LanguageWrapper>{isAdminUser(user) ? <AdminMcpDocs user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/production" element={<LanguageWrapper>{isAdminUser(user) ? <AdminProduction user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/inventory-import" element={<LanguageWrapper>{isAdminUser(user) ? <AdminInventoryImport user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/discount-groups" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'clients.view') ? <AdminDiscountGroups user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/products" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'products.view') ? <AdminProductsPro user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/price-list" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'products.view') ? <AdminPriceList user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/settings" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, ['settings.view', 'settings.edit']) ? <AdminSettings user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/payments" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'payments.view') ? <AdminPayments user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/finance" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, ['payments.view', 'payments.edit']) ? <AdminFinance user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/support" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'support.view') ? <AdminSupport user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/delivery" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'delivery.view') ? <AdminDelivery user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/delivery-zones" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, ['settings.view', 'settings.edit']) ? <AdminDeliveryZones user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/containers" element={<LanguageWrapper>{isAdminUser(user) ? <AdminContainers user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/containers-config" element={<LanguageWrapper>{isAdminUser(user) ? <AdminContainersConfig user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/admin/containers-config" element={isAdminUser(user) ? <AdminContainersConfig user={user} onLogout={onLogout} /> : <LangRedirect />} />
      <Route path="/:lang/admin/route-editor" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'delivery.routes') ? <AdminRouteEditor user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/admin/route-editor" element={isAdminUser(user) && hasPermission(user?.permissions, 'delivery.routes') ? <AdminRouteEditor user={user} onLogout={onLogout} /> : <LangRedirect />} />
      <Route path="/:lang/payment/success" element={<LanguageWrapper><PaymentSuccess /></LanguageWrapper>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/:lang/payment/failure" element={<LanguageWrapper><PaymentFailure /></LanguageWrapper>} />
      <Route path="/payment/failure" element={<PaymentFailure />} />
      <Route path="/:lang/legal/:slug" element={<LanguageWrapper><LegalDocument /></LanguageWrapper>} />
      <Route path="/legal/:slug" element={<LegalDocument />} />
      <Route path="/:lang/admin/legal-pages" element={<LanguageWrapper>{isAdminUser(user) ? <AdminLegalPages user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/admin/legal-pages" element={isAdminUser(user) ? <AdminLegalPages user={user} onLogout={onLogout} /> : <LangRedirect />} />
      <Route path="/:lang/admin/contracts" element={<LanguageWrapper>{isAdminUser(user) ? <AdminContracts user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/admin/contracts" element={isAdminUser(user) ? <AdminContracts user={user} onLogout={onLogout} /> : <LangRedirect />} />
      <Route path="/:lang/admin/integrations" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'integrations.view') ? <AdminIntegrations user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/security" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'users.view') ? <AdminSecurity user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/notifications" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'settings.view') ? <AdminNotifications user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/cities" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'settings.view') ? <AdminCities user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/cities/:cityId/prices" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'settings.view') ? <AdminCityPrices user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/analytics" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'analytics.view') ? <AnalyticsPowerBI user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/content-creators" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, 'users.view') ? <AdminContentCreators /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/admin/content" element={<LanguageWrapper>{isAdminUser(user) && hasPermission(user?.permissions, ['content.view', '*']) ? <AdminContent /> : <LangRedirect />}</LanguageWrapper>} />
      <Route path="/:lang/media-studio" element={<LanguageWrapper>{!user ? <Navigate to={`/${getCurrentLang()}/login`} /> : hasPermission(user?.permissions, ['content.view', 'content.create', '*']) ? <MediaStudio /> : <Navigate to={`/${getCurrentLang()}`} />}</LanguageWrapper>} />
      <Route path="/:lang/production" element={<Navigate to={`/${getCurrentLang()}/admin/production`} replace />} />
      
      {/* Public shared gallery - no auth required */}
      <Route path="/share/:token" element={<SharedGallery />} />
      
      {/* Public B2B wholesale menu - no auth required */}
      <Route path="/b2b-menu" element={<B2BMenu />} />
      <Route path="/:lang/b2b-menu" element={<LanguageWrapper><B2BMenu /></LanguageWrapper>} />
      
      {/* B2B Networks */}
      <Route path="/:lang/b2b/networks" element={<LanguageWrapper>{user?.role === 'B2B' ? <B2BNetworks user={user} onLogout={onLogout} /> : <LangRedirect />}</LanguageWrapper>} />
      
      {/* Legacy admin routes redirect to language-prefixed versions */}
      <Route path="/admin" element={<LangRedirect to="/admin" />} />
      <Route path="/admin/orders" element={<LangRedirect to="/admin/orders" />} />
      <Route path="/admin/clients" element={<LangRedirect to="/admin/clients" />} />
      <Route path="/admin/products" element={<LangRedirect to="/admin/products" />} />
      <Route path="/admin/settings" element={<LangRedirect to="/admin/settings" />} />
      <Route path="/admin/payments" element={<LangRedirect to="/admin/payments" />} />
      <Route path="/production" element={<LangRedirect to="/production" />} />
      <Route path="/legal-entities" element={<LangRedirect to="/legal-entities" />} />
      
      {/* Legacy routes redirect to language-prefixed versions */}
      <Route path="/about" element={<LangRedirect to="/about" />} />
      <Route path="/contacts" element={<LangRedirect to="/contacts" />} />
      <Route path="/login" element={<LangRedirect to="/login" />} />
      <Route path="/register" element={<LangRedirect to="/register" />} />
      <Route path="/forgot-password" element={<LangRedirect to="/forgot-password" />} />
      <Route path="/reset-password" element={<LangRedirect to="/reset-password" />} />
      <Route path="/profile" element={<LangRedirect to="/profile" />} />
      <Route path="/settings" element={<LangRedirect to="/settings" />} />
      <Route path="/orders" element={<LangRedirect to="/orders" />} />
      <Route path="/addresses" element={<LangRedirect to="/addresses" />} />
      <Route path="/analytics" element={<LangRedirect to="/analytics" />} />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<LangRedirect />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Global axios interceptor: catch expired/invalid JWT on ANY request
  // (product edits, recipe saves, uploads, etc.) — clear token, tell user, redirect to login.
  // Skipped for public/anon flows: login, register, verify-code, forgot-password (they use 401
  // as a normal validation signal, not as a session-expired signal).
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const status = error?.response?.status;
        const url = (error?.config?.url || '');
        const isApi = url.includes('/api/');
        const isAuthFlow = /\/api\/(auth\/(login|register|send-verification-code|verify-code|forgot-password|reset-password|2fa)|admin\/1c\/login)/.test(url);
        // Ignore the initial /auth/me probe — App.checkAuth handles that itself
        const isAuthMeProbe = url.endsWith('/auth/me');
        if (status === 401 && isApi && !isAuthFlow && !isAuthMeProbe) {
          const hadToken = !!localStorage.getItem('token');
          if (hadToken) {
            localStorage.removeItem('token');
            setUser(null);
            const lang = (window.location.pathname.match(/^\/([a-z]{2})(?:\/|$)/) || [])[1] || 'ru';
            // Preserve current URL to bounce back after re-login
            const from = window.location.pathname + window.location.search;
            const target = `/${lang}/login?from=${encodeURIComponent(from)}`;
            // Flag the toast synchronously so it survives the hard redirect below.
            // Login.js reads it on mount and shows the actual Sonner toast.
            try { sessionStorage.setItem('__session_expired_toast', '1'); } catch { /* quota / storage disabled */ }
            if (!window.location.pathname.endsWith('/login')) {
              window.location.replace(target);
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        // Only clear token on 401 (expired/invalid), not on network/server errors
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes user={user} setUser={setUser} onLogout={handleLogout} loading={loading} />
        {/* Виджет поддержки скрыт в приложении водителя (/driver) */}
        {!window.location.pathname.startsWith('/driver') && !window.location.pathname.startsWith('/analytics-app') && !window.location.pathname.startsWith('/production-app') && !window.location.pathname.startsWith('/finance-app') && !window.location.pathname.startsWith('/support-app') && !window.location.pathname.startsWith('/tg-support') && <SupportWidget />}
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
